import React, { useCallback, useMemo, useState } from 'react';
import s from './WorkTracker.module.scss';
import useTrackerData from './useTrackerData';
import { api } from './api';
import Toolbar from './Toolbar';
import TaskList from './TaskList';
import TaskDialog from './TaskDialog';
import NewTaskPanel from './NewTaskPanel';
import CalendarView from './CalendarView';
import ConfirmDialog from './ConfirmDialog';
import Toast from './Toast';
import { startOfToday } from './lib/dates';
import { filterTasks, moveItem, sortTasks } from './lib/sorting';

const SYNC_TEXT = {
  loading: 'Loading', synced: 'Synced', syncing: 'Syncing', offline: 'Offline',
};
const EMPTY_TEXT = {
  active: 'Nothing active. Add a task to get started.',
  complete: 'Nothing completed yet.',
};

export default function WorkTracker() {
  const {
    data, sync, refresh, putTask, dropTask, putEvent, dropEvent, setData,
  } = useTrackerData();
  const [view, setView] = useState('list');
  const [list, setList] = useState('active');
  const [sort, setSortKey] = useState('due');
  const [sortReverse, setSortReverse] = useState(false);
  const [sortExplicit, setSortExplicit] = useState(false); // chosen by the viewer, not a list default
  const [search, setSearch] = useState('');
  const [openTaskId, setOpenTaskId] = useState(null);
  const [newTask, setNewTask] = useState(null); // null | { title?, due_on? }
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [month, setMonth] = useState(() => {
    const t = startOfToday();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const today = startOfToday();
  const todayKey = today.getTime();

  const tasks = data?.tasks || [];
  const people = data?.people || [];
  const visible = useMemo(
    () => sortTasks(filterTasks(tasks, list, search), sort, new Date(todayKey), sortReverse),
    [tasks, list, search, sort, sortReverse, todayKey],
  );
  // Picking a sort (dropdown or drag) lists it in its natural order; only a
  // second click on the same column header flips it.
  const applySort = (next, explicit) => { setSortKey(next); setSortReverse(false); setSortExplicit(explicit); };
  const setSort = (next) => applySort(next, true);
  const sortByColumn = (next) => {
    if (next === sort) { setSortReverse((r) => !r); setSortExplicit(true); } else setSort(next);
  };
  // Each list has a default sort (Completed: recently completed, Active: due
  // date) that applies until the viewer picks one; a pick survives the toggle
  // even when it happens to equal the other list's default.
  const changeList = (next) => {
    setList(next);
    if (!sortExplicit) applySort(next === 'complete' ? 'completed' : 'due', false);
  };
  const openTask = tasks.find((t) => t.id === openTaskId) || null;

  const ok = (text) => setToast({ text });
  const fail = useCallback((e) => setToast({ text: e.message || 'Something went wrong', tone: 'error' }), []);
  const closeDialog = useCallback(() => setOpenTaskId(null), []);
  const closeNew = useCallback(() => setNewTask(null), []);
  const closeConfirm = useCallback(() => setConfirm(null), []);
  const clearToast = useCallback(() => setToast(null), []);

  // Every write: apply the response straight away, then let the poll confirm.
  const updateTask = async (task, attrs, extra, message = 'Saved.') => {
    try {
      putTask(await api.updateTask(task.id, attrs, extra));
      ok(message);
      return true;
    } catch (e) {
      fail(e);
      return false;
    }
  };
  const changeStatus = (task, status) => updateTask(task, { status });
  const saveFields = (task, fields) => updateTask(task, fields);
  const saveDescription = (task, description, release) => (
    updateTask(task, { description }, { release_lock: !!release })
  );
  const changeChecklist = (task, checklist) => updateTask(task, { checklist });
  const createTask = async (attrs) => {
    try {
      putTask(await api.createTask(attrs));
      ok('Task added.');
      return true;
    } catch (e) {
      fail(e);
      return false;
    }
  };
  const deleteTask = (task) => setConfirm({
    title: 'Delete this task?',
    text: `"${task.title}" and its comments will be removed for everyone.`,
    onConfirm: async () => {
      try {
        await api.deleteTask(task.id);
        dropTask(task.id);
        if (openTaskId === task.id) closeDialog();
        ok('Deleted.');
      } catch (e) {
        fail(e);
      }
    },
  });
  const addComment = async (task, body) => {
    try {
      await api.addComment(task.id, body);
      await refresh();
      ok('Comment added.');
      return true;
    } catch (e) {
      fail(e);
      return false;
    }
  };
  const deleteComment = (task, comment) => setConfirm({
    title: 'Delete this comment?',
    onConfirm: async () => {
      try {
        await api.deleteComment(task.id, comment.id);
        await refresh();
        ok('Deleted.');
      } catch (e) {
        fail(e);
      }
    },
  });

  // Drag: the visible order becomes the manual order, with the move applied.
  const reorder = async (from, to) => {
    const ids = moveItem(visible, from, to).map((t) => t.id);
    setSort('manual');
    setData((d) => d && {
      ...d,
      tasks: d.tasks.map((t) => (
        ids.includes(t.id) ? { ...t, position: ids.indexOf(t.id) } : { ...t, position: ids.length + (t.position || 0) }
      )),
    });
    try {
      setData(await api.reorder(ids));
      ok('Order saved.');
    } catch (e) {
      fail(e);
      refresh();
    }
  };

  const addEvent = async (attrs) => {
    try {
      putEvent(await api.createEvent(attrs));
      ok('Event added.');
      return true;
    } catch (e) {
      fail(e);
      return false;
    }
  };
  const renameEvent = async (event, name) => {
    try {
      putEvent(await api.updateEvent(event.id, { name }));
      ok('Saved.');
      return true;
    } catch (e) {
      fail(e);
      return false;
    }
  };
  const deleteEvent = (event) => setConfirm({
    title: 'Delete this event?',
    text: `"${event.name}" will be removed from the calendar for everyone.`,
    onConfirm: async () => {
      try {
        await api.deleteEvent(event.id);
        dropEvent(event.id);
        ok('Deleted.');
      } catch (e) {
        fail(e);
      }
    },
  });

  const openFromCalendar = (task) => { setView('list'); setOpenTaskId(task.id); };
  const emptyText = search ? 'No tasks match your search.' : EMPTY_TEXT[list];
  const syncTitle = sync === 'offline' ? 'Could not reach the server; retrying' : undefined;

  return (
    <div className={s.root}>
      <div className={s.inner}>
        <div className={s.header}>
          <div>
            <h1 className={s.title}>Work tracker</h1>
            <p className={s.subtitle}>
              Everything we&apos;re working on, in one shared list. Changes save automatically and everyone
              sees them live.
            </p>
          </div>
          <span className={s.sync} title={syncTitle}>
            <span className={s.syncDot} data-state={sync} />
            {' '}
            {SYNC_TEXT[sync]}
          </span>
        </div>

        <Toolbar
          view={view}
          onView={setView}
          list={list}
          onList={changeList}
          sort={sort}
          onSort={setSort}
          search={search}
          onSearch={setSearch}
          onNewTask={() => setNewTask({})}
        />

        {!data && sync === 'loading' && <div className={s.empty}>Loading…</div>}
        {!data && sync === 'offline' && <div className={s.empty}>Could not load the list. Retrying…</div>}
        {data && view === 'list' && (
          <TaskList
            tasks={visible}
            today={today}
            openTaskId={openTaskId}
            sort={sort}
            sortReverse={sortReverse}
            onSortColumn={sortByColumn}
            onOpen={(t) => setOpenTaskId(t.id)}
            onStatus={changeStatus}
            onReorder={reorder}
            emptyText={emptyText}
          />
        )}
        {data && view === 'calendar' && (
          <CalendarView
            tasks={visible}
            events={data.events || []}
            today={today}
            month={month}
            onMonth={setMonth}
            onOpenTask={openFromCalendar}
            onAddEvent={addEvent}
            onRenameEvent={renameEvent}
            onDeleteEvent={deleteEvent}
            onTaskInstead={(prefill) => setNewTask(prefill)}
            undatedCount={visible.filter((t) => !t.due_on).length}
            onShowList={() => setView('list')}
          />
        )}
      </div>

      {openTask && (
        <TaskDialog
          key={openTask.id}
          task={openTask}
          people={people}
          onClose={closeDialog}
          onStatus={changeStatus}
          onSaveFields={saveFields}
          onSaveDescription={saveDescription}
          onChecklist={changeChecklist}
          onAddComment={addComment}
          onDeleteComment={deleteComment}
          onDelete={deleteTask}
          onError={fail}
        />
      )}
      {newTask && (
        <NewTaskPanel
          prefill={newTask}
          people={people}
          onClose={closeNew}
          onCreate={createTask}
        />
      )}
      <ConfirmDialog confirm={confirm} onClose={closeConfirm} />
      <Toast toast={toast} onDone={clearToast} />
    </div>
  );
}
